const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

/**
 * Utility functions for question manager bulk upload
 */

export const downloadCSVTemplate = () => {
    const csv = 'Question,Option A,Option B,Option C,Option D,Correct Answer (A/B/C/D),Difficulty (Easy/Medium/Hard),Marks (1-10)\n'
        + '"Sample: What is 2+2?","4","5","6","3","A","Easy","1"\n';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'questions_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
};

export const parseCSVFile = async (file) => {
    try {
        const text = await file.text();
        // Basic CSV parsing that handles quoted fields and commas inside quotes.
        const rawLines = text.split(/\r?\n/);
        // Remove leading/trailing blank lines
        while (rawLines.length && rawLines[0].trim() === '') rawLines.shift();
        while (rawLines.length && rawLines[rawLines.length - 1].trim() === '') rawLines.pop();

        if (rawLines.length < 2) {
            return { rows: [], errors: ['File must have header and at least 1 data row'] };
        }

        const header = rawLines[0];
        const dataLines = rawLines.slice(1);
        const errors = [];
        const rows = [];

        const splitLine = (line) => {
            const parts = [];
            let cur = '';
            let inQuotes = false;
            for (let i = 0; i < line.length; i++) {
                const ch = line[i];
                if (ch === '"') {
                    if (inQuotes && line[i + 1] === '"') {
                        cur += '"';
                        i++; // skip escaped quote
                    } else {
                        inQuotes = !inQuotes;
                    }
                } else if (ch === ',' && !inQuotes) {
                    parts.push(cur);
                    cur = '';
                } else {
                    cur += ch;
                }
            }
            parts.push(cur);
            return parts.map(p => p.trim().replace(/^\"|\"$/g, ''));
        };

        dataLines.forEach((line, idx) => {
            if (!line.trim()) return; // skip empty
            const parts = splitLine(line);
            if (parts.length < 8) {
                errors.push(`Row ${idx + 2}: Incomplete data`);
                return;
            }

            rows.push({
                prompt: parts[0],
                optionA: parts[1],
                optionB: parts[2],
                optionC: parts[3],
                optionD: parts[4],
                correctOption: (parts[5] || '').toUpperCase(),
                difficulty: parts[6] || 'Easy',
                marks: parseInt(parts[7]) || 1,
            });
        });

        return { rows, errors };
    } catch (error) {
        return { rows: [], errors: ['Error parsing file: ' + error.message] };
    }
};

export const submitBulkQuestions = async (parsedQuestions, selectedTopic, token) => {
    try {
        if (!selectedTopic) {
            return { success: false, error: 'Topic not selected' };
        }

        if (!Array.isArray(parsedQuestions) || parsedQuestions.length === 0) {
            return { success: false, error: 'No valid questions to submit' };
        }

        // Transform parsed questions to backend format
        const questions = parsedQuestions.map(q => ({
            topic: selectedTopic,
            prompt: q.prompt,
            option_a: q.optionA,
            option_b: q.optionB,
            option_c: q.optionC,
            option_d: q.optionD,
            correct_option: q.correctOption,
            difficulty: q.difficulty,
            marks: q.marks,
            status: 'submitted'
        }));

        // Call bulk upload endpoint
        const response = await fetch(`${API_BASE_URL}/api/admin-app/aptitude-questions/bulk/create/`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({ questions }),
        });

        const responseText = await response.text();
        let data = null;
        try {
            data = responseText ? JSON.parse(responseText) : null;
        } catch (error) {
            data = null;
        }

        if (!response.ok) {
            return { 
                success: false, 
                error: (data && data.error) || responseText || 'Failed to submit questions',
                details: data || { raw: responseText }
            };
        }

        return { 
            success: true, 
            created: data?.created || 0,
            failed: data?.failed || 0,
            results: data || { raw: responseText }
        };
    } catch (error) {
        return { success: false, error: 'Network error: ' + error.message };
    }
};
